import React from 'react';
import {
  Box,
  Checkbox,
  Chip,
  Divider,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Theme,
  Typography,
} from '@material-ui/core';
import { makeStyles, createStyles } from '@material-ui/styles';
import { CheckCircle, ErrorOutline, RadioButtonUnchecked } from '@material-ui/icons';
import { TabletButton } from '@campfire/tablet-button';
import { SearchField } from '../../../../../common/inputs/SearchField';
import { FullscreenDialog } from '../../../../../common/dialogs/FullscreenDialog';
import { useCampfireQuery } from '../../../../../global/network/useCampfireQuery';
import { useCampfireFetch } from '../../../../../global/network/useCampfireFetch';
import { useApiUrl } from '../../../../../global/config/useApiUrl';
import { GET_SUB_ACCOUNT_ENTITIES } from './enterprise-sub-account-entities.gql';
import { EntityPicker, EntitySelection, countSelected } from './EntityPicker';
import { GetSubAccountEntities, GetSubAccountEntitiesVariables } from './__generated__/GetSubAccountEntities';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    bodyPanel: {
      fontSize: '14px',
      color: '#808080',
    },
    label: {
      fontSize: '13px',
      fontWeight: 600,
      color: theme.color.grey.neutral400,
      paddingTop: '1.25rem',
      paddingBottom: '0.5rem',
    },
    chips: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: 6,
    },
    chip: {
      fontSize: '12px',
      color: theme.color.grey.neutral400,
    },
    name: {
      fontSize: '14px',
      color: theme.color.grey.neutralBrand900,
    },
    secondary: {
      fontSize: '12px',
      color: theme.color.grey.neutral200,
    },
    resultIcon: {
      fontSize: '18px',
    },
  })
);

export interface ShareTarget {
  tenantId: string;
  name: string;
  programNames: string[];
}

type Step = 'items' | 'subs' | 'results';

interface SubResult {
  tenantId: string;
  name: string;
  status: 'waiting' | 'done' | 'error';
  added: number;
  skipped: number;
  message?: string;
}

interface Props {
  open: boolean;
  subAccounts: ShareTarget[];
  initialSubTenantIds?: string[];
  onClose: () => void;
  onShared: () => void;
}

export const ShareItemsDialog = (props: Props) => {
  const { open, subAccounts, initialSubTenantIds = [], onClose, onShared } = props;
  const classes = useStyles();
  const apiUrl = useApiUrl();
  const campfireMutation = useCampfireFetch({ defer: true });

  const [step, setStep] = React.useState<Step>('items');
  const [selected, setSelected] = React.useState<EntitySelection>({});
  const [subIds, setSubIds] = React.useState<string[]>(initialSubTenantIds);
  const [subSearch, setSubSearch] = React.useState('');
  const [results, setResults] = React.useState<SubResult[]>([]);
  const [running, setRunning] = React.useState(false);

  const { data, loading } = useCampfireQuery<GetSubAccountEntities, GetSubAccountEntitiesVariables>(
    GET_SUB_ACCOUNT_ENTITIES,
    { options: { variables: { subTenantId: null } } }
  );

  const entities = data?.vm.enterpriseSubAccountEntities ?? [];
  const selectedCount = countSelected(selected);

  const term = subSearch.trim().toLowerCase();
  const visibleSubs = subAccounts.filter((sub) => term === '' || sub.name.toLowerCase().includes(term));
  const allVisibleSelected = visibleSubs.length > 0 && visibleSubs.every((sub) => subIds.includes(sub.tenantId));
  const someVisibleSelected = visibleSubs.some((sub) => subIds.includes(sub.tenantId));

  const programs = subAccounts
    .flatMap((sub) => sub.programNames)
    .filter((name, index, all) => all.indexOf(name) === index)
    .sort((a, b) => a.localeCompare(b));

  const subsWithProgram = (programName: string) =>
    subAccounts.filter((sub) => sub.programNames.includes(programName)).map((sub) => sub.tenantId);

  const toggleSub = (tenantId: string) =>
    setSubIds((current) =>
      current.includes(tenantId) ? current.filter((id) => id !== tenantId) : [...current, tenantId]
    );

  const toggleAllVisible = () => {
    const visibleIds = visibleSubs.map((sub) => sub.tenantId);

    setSubIds((current) =>
      allVisibleSelected
        ? current.filter((id) => !visibleIds.includes(id))
        : [...current, ...visibleIds.filter((id) => !current.includes(id))]
    );
  };

  const addSubs = (tenantIds: string[]) =>
    setSubIds((current) => [...current, ...tenantIds.filter((id) => !current.includes(id))]);

  const stampOne = async (sub: ShareTarget): Promise<SubResult> => {
    try {
      const res: any = await campfireMutation.run({
        url: `${apiUrl}/vm/enterprise/sub-accounts/stamp`,
        method: 'post',
        data: { subTenantId: sub.tenantId, selection: selected },
      });

      if (!res?.ok) {
        return {
          tenantId: sub.tenantId,
          name: sub.name,
          status: 'error',
          added: 0,
          skipped: 0,
          message: res?.data?.message || 'Could not copy the selection',
        };
      }

      const outcome = res?.data?.data ?? {};
      const addedByType: Record<string, number> = outcome.added ?? {};
      const added = Object.values(addedByType).reduce((total, count) => total + Number(count), 0);

      return {
        tenantId: sub.tenantId,
        name: sub.name,
        status: 'done',
        added,
        skipped: Number(outcome.skipped ?? 0),
      };
    } catch (e) {
      return {
        tenantId: sub.tenantId,
        name: sub.name,
        status: 'error',
        added: 0,
        skipped: 0,
        message: 'Could not copy the selection',
      };
    }
  };

  const handleShare = async () => {
    const targets = subAccounts.filter((sub) => subIds.includes(sub.tenantId));

    setStep('results');
    setRunning(true);
    setResults(
      targets.map((sub) => ({ tenantId: sub.tenantId, name: sub.name, status: 'waiting', added: 0, skipped: 0 }))
    );

    await targets.reduce(
      (previous, sub) =>
        previous.then(async () => {
          const result = await stampOne(sub);
          setResults((current) => current.map((r) => (r.tenantId === result.tenantId ? result : r)));
        }),
      Promise.resolve()
    );

    setRunning(false);
    onShared();
  };

  const handleClose = () => {
    if (running) return;
    onClose();
  };

  const finished = results.filter((r) => r.status !== 'waiting').length;
  const failed = results.filter((r) => r.status === 'error').length;

  const describe = (result: SubResult) => {
    if (result.status === 'waiting') return 'Waiting…';
    if (result.status === 'error') return result.message;

    const parts = [`${result.added} added`];
    if (result.skipped > 0) parts.push(`${result.skipped} already there`);

    return parts.join(', ');
  };

  const actions = () => {
    if (step === 'items') {
      return (
        <>
          <TabletButton onClick={handleClose} style={{ marginRight: '8px' }}>
            Cancel
          </TabletButton>
          <TabletButton
            variant='contained'
            color='primary'
            onClick={() => setStep('subs')}
            disabled={selectedCount === 0}
          >
            {`Next${selectedCount > 0 ? ` (${selectedCount})` : ''}`}
          </TabletButton>
        </>
      );
    }

    if (step === 'subs') {
      return (
        <>
          <TabletButton onClick={() => setStep('items')} style={{ marginRight: '8px' }}>
            Back
          </TabletButton>
          <TabletButton variant='contained' color='primary' onClick={handleShare} disabled={subIds.length === 0}>
            {`Share with ${subIds.length} sub-account${subIds.length !== 1 ? 's' : ''}`}
          </TabletButton>
        </>
      );
    }

    return (
      <TabletButton variant='contained' color='primary' onClick={handleClose} disabled={running}>
        Done
      </TabletButton>
    );
  };

  if (!open) return null;

  return (
    <FullscreenDialog open close={handleClose} title='Share Items' maxWidth='sm' fullWidth dialogActions={actions}>
      <Box mt='20px'>
        {step === 'items' && (
          <>
            <Typography variant='body2' className={classes.bodyPanel}>
              {
                "Choose what to share. Anything a selection depends on, such as a task's category, is copied with it. Sub-accounts that already have an item keep theirs."
              }
            </Typography>
            <EntityPicker
              entities={entities}
              loading={loading || data === undefined}
              selected={selected}
              onChange={setSelected}
              emptyMessage='There is nothing in this account to share yet.'
            />
          </>
        )}

        {step === 'subs' && (
          <>
            <Typography variant='body2' className={classes.bodyPanel}>
              {`Choose the sub-accounts to receive ${selectedCount} item${selectedCount !== 1 ? 's' : ''}.`}
            </Typography>

            {programs.length > 0 && (
              <>
                <Typography className={classes.label}>Quick select by program</Typography>
                <Box className={classes.chips}>
                  {programs.map((programName) => (
                    <Chip
                      key={programName}
                      size='small'
                      variant='outlined'
                      className={classes.chip}
                      label={`All with ${programName} (${subsWithProgram(programName).length})`}
                      onClick={() => addSubs(subsWithProgram(programName))}
                    />
                  ))}
                </Box>
              </>
            )}

            {subAccounts.length > 8 && (
              <Box paddingTop='1rem'>
                <SearchField
                  placeholder='Search sub-accounts'
                  fullWidth
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSubSearch(e.target.value)}
                />
              </Box>
            )}

            <List disablePadding style={{ paddingTop: '0.75rem' }}>
              <ListItem button dense disableGutters onClick={toggleAllVisible}>
                <ListItemIcon style={{ minWidth: '40px' }}>
                  <Checkbox
                    edge='start'
                    color='primary'
                    checked={allVisibleSelected}
                    indeterminate={someVisibleSelected && !allVisibleSelected}
                    tabIndex={-1}
                    disableRipple
                  />
                </ListItemIcon>
                <ListItemText
                  primary={`Select all (${subIds.length} of ${subAccounts.length} selected)`}
                  primaryTypographyProps={{ className: classes.name }}
                />
              </ListItem>
              <Divider />
              {visibleSubs.map((sub) => (
                <ListItem key={sub.tenantId} button dense disableGutters onClick={() => toggleSub(sub.tenantId)}>
                  <ListItemIcon style={{ minWidth: '40px' }}>
                    <Checkbox
                      edge='start'
                      color='primary'
                      checked={subIds.includes(sub.tenantId)}
                      tabIndex={-1}
                      disableRipple
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={sub.name}
                    secondary={sub.programNames.length > 0 ? sub.programNames.join(', ') : 'No programs'}
                    primaryTypographyProps={{ className: classes.name }}
                    secondaryTypographyProps={{ className: classes.secondary }}
                  />
                </ListItem>
              ))}
              {visibleSubs.length === 0 && (
                <Typography variant='body2' className={classes.bodyPanel} style={{ paddingTop: '1rem' }}>
                  {`No sub-accounts match '${subSearch.trim()}'.`}
                </Typography>
              )}
            </List>
          </>
        )}

        {step === 'results' && (
          <>
            <Typography variant='body2' className={classes.bodyPanel}>
              {running
                ? `Sharing with ${Math.min(finished + 1, results.length)} of ${results.length}…`
                : `Finished. ${results.length - failed} of ${results.length} sub-account${
                    results.length !== 1 ? 's' : ''
                  } updated${failed > 0 ? `, ${failed} failed` : ''}.`}
            </Typography>
            <LinearProgress
              variant='determinate'
              value={results.length === 0 ? 0 : (finished / results.length) * 100}
              style={{ marginTop: '1rem' }}
            />
            <List disablePadding style={{ paddingTop: '0.75rem' }}>
              {results.map((result) => (
                <ListItem key={result.tenantId} dense disableGutters>
                  <ListItemIcon style={{ minWidth: '32px' }}>
                    {result.status === 'done' && (
                      <CheckCircle className={classes.resultIcon} style={{ color: '#2e7d32' }} />
                    )}
                    {result.status === 'error' && (
                      <ErrorOutline className={classes.resultIcon} style={{ color: '#c62828' }} />
                    )}
                    {result.status === 'waiting' && (
                      <RadioButtonUnchecked className={classes.resultIcon} style={{ color: '#bdbdbd' }} />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary={result.name}
                    secondary={describe(result)}
                    primaryTypographyProps={{ className: classes.name }}
                    secondaryTypographyProps={{ className: classes.secondary }}
                  />
                </ListItem>
              ))}
            </List>
          </>
        )}
      </Box>
    </FullscreenDialog>
  );
};
